import { Link } from "react-router-dom";
import { ArrowRight, Download, FileCode2, Info, Sliders } from "lucide-react";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { CopyButton } from "@/components/ui/CopyButton";
import { Reveal } from "@/components/ui/Reveal";
import { toast } from "sonner";

export const DownloadsBlock = ({ builds = [], config, showPresetLink = true }) => {
  const notReady = (name) =>
    toast.info(
      `${name} is released to licensed accounts. Send us your reference code on Telegram and we unlock your download.`
    );

  return (
    <section id="downloads" className="gm-section" style={{ background: "var(--gm-navy-950)" }}>
      <div className="gm-container">
        <SectionHeader
          eyebrow="Downloads"
          title="Grab the build for your terminal"
          subtitle="Compiled for MetaTrader 4 and 5. Drop it into your Experts folder, then activate with your licence key."
          align="center"
          testId="downloads-header"
        />

        <div className="mt-12 grid gap-5 md:grid-cols-2">
          {builds.map((b, i) => (
            <Reveal key={b.slug} delay={i * 70}>
              <article
                className="gm-card gm-card-hover h-full"
                data-testid={`downloads-${b.slug}-card`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-bold text-white">{b.platform}</h3>
                    <p
                      className="font-mono-gm mt-1.5 text-xs"
                      style={{ color: "var(--gm-muted)" }}
                    >
                      {b.filename}
                    </p>
                  </div>
                  <span
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl"
                    style={{
                      background: "rgba(76,195,255,0.1)",
                      border: "1px solid rgba(76,195,255,0.28)",
                    }}
                  >
                    <FileCode2 className="h-5 w-5" style={{ color: "var(--gm-info)" }} />
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="gm-chip">{b.version}</span>
                  <span className="gm-chip">{b.size_label}</span>
                  <span className="gm-chip">{b.note}</span>
                </div>

                <button
                  type="button"
                  onClick={() => notReady(b.filename)}
                  data-testid={`downloads-${b.slug}-button`}
                  className="gm-btn-red mt-5 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl text-sm font-bold"
                >
                  <Download className="h-4 w-4" />
                  Download for {b.slug.toUpperCase()}
                </button>
              </article>
            </Reveal>
          ))}
        </div>

        {showPresetLink ? (
          <Reveal delay={140}>
            <Link
              to="/downloads"
              data-testid="downloads-presets-link"
              className="gm-card gm-card-hover mt-5 flex items-center gap-4"
            >
              <span
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                style={{
                  background: "rgba(246,196,83,0.1)",
                  border: "1px solid rgba(246,196,83,0.3)",
                }}
              >
                <Sliders className="h-5 w-5" style={{ color: "var(--gm-warning)" }} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-white">
                  Need the preset .set files?
                </span>
                <span className="mt-1 block text-xs" style={{ color: "var(--gm-muted)" }}>
                  Ready-made presets for MT4 and MT5 at 1:500 and 1:2000 leverage, tuned
                  around a 50K reference capital.
                </span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0" style={{ color: "var(--gm-muted-2)" }} />
            </Link>
          </Reveal>
        ) : null}

        <Reveal delay={180}>
          <div
            className="mt-5 rounded-xl p-5"
            data-testid="downloads-setup-note"
            style={{
              background: "rgba(76,195,255,0.06)",
              border: "1px solid rgba(76,195,255,0.26)",
            }}
          >
            <div className="flex items-start gap-3">
              <Info
                className="mt-0.5 h-5 w-5 shrink-0"
                style={{ color: "var(--gm-info)" }}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-bold text-white">
                  Setup note - do this once per terminal
                </h3>
                <p className="mt-1.5 text-xs leading-relaxed" style={{ color: "var(--gm-muted)" }}>
                  The EA verifies your licence online, so MetaTrader must be allowed to
                  reach our licence domain. Kalau tak set, the EA will not trade.
                </p>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <code
                    className="font-mono-gm rounded-lg px-3 py-2 text-xs text-white"
                    data-testid="downloads-whitelist-url"
                    style={{
                      background: "rgba(7,11,29,0.7)",
                      border: "1px solid rgba(255,255,255,0.14)",
                    }}
                  >
                    {config?.whitelist_url || "https://license.goldmapea.com"}
                  </code>
                  <CopyButton
                    value={config?.whitelist_url || "https://license.goldmapea.com"}
                    label="Copy URL"
                    testId="downloads-whitelist-copy-button"
                  />
                </div>

                <p className="mt-4 text-xs leading-relaxed" style={{ color: "var(--gm-muted-2)" }}>
                  In MetaTrader: Tools &rarr; Options &rarr; Expert Advisors &rarr; tick
                  &ldquo;Allow WebRequest for listed URL&rdquo; &rarr; paste the domain
                  above &rarr; OK.
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        <p className="mt-6 text-center text-xs" style={{ color: "var(--gm-muted-2)" }}>
          No licence yet?{" "}
          <a
            href="/#pricing"
            data-testid="downloads-pricing-link"
            className="font-semibold text-white underline decoration-white/40 underline-offset-2 hover:decoration-white"
          >
            Check the options above
          </a>
          .
        </p>
      </div>
    </section>
  );
};
